import { makeStyles } from 'tss-react/mui';
import { P2GBox, P2GCardContainer, P2GGrid, P2GText, useP2GMediaQueryUp } from '@parcel2go/core-ui';
import useBasket from '~/hooks/use-basket';
import useQuote from '~/hooks/use-quote';
import { DefaultServiceBenefits } from './default-service-benefits/default-service-benefits';
import QuotePrice from './quote-price/quote-price';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { Suspense, lazy, useState } from 'react';
import { doesQuoteRequirePrinter, isQuoteDropOff, isQuoteLockerDropOff } from '~/lib/quotes';
import { useProtection } from '~/hooks/use-protection';
import FeedbackButton from '~/components/feedback/feedback-button';
import LiveChat from '~/components/basket-summary/basket/live-chat';
import QuoteCardServiceLogo from '~/components/quote-results/card/quote-card-logo';

const CmsContent = lazy(() => import('~/components/core/content/cms-content'));
const BasketServiceSwitcher = lazy(() => import('~/components/core/switcher/basket-service-switcher'));

export type CustomerBasketSection = {
  title: string;
  forename: string;
  surname: string;
  email: string;
  optIns: { [name: string]: boolean };
};

const useStyles = makeStyles()((theme) => ({
  root: {
    // The card comes with padding but the prices need to span full width
    '& .MuiCardContent-root': {
      padding: '0 !important'
    }
  },
  cardHeader: {
    backgroundColor: theme.palette.grey.A100
  }
}));

function CheckoutSidebar() {
  const { item } = useBasket();
  const { quote } = useQuote();
  const { protection } = useProtection();
  const { classes } = useStyles();
  const { t } = useTranslation(TranslationNamespaces.CHECKOUT);
  const [openSwitcher, setOpenSwitcher] = useState(false);
  const showServiceInfo = useP2GMediaQueryUp('md');

  if (!quote) return null;

  const isDropOff = isQuoteDropOff(quote);
  const requiresPrinter = doesQuoteRequirePrinter(quote);

  return (
    <>
      <P2GCardContainer classNames={[classes.root]} dataTestId="sidebar">
        <P2GGrid container direction="column" spacing={1}>
          <P2GGrid item>
            <P2GGrid
              container
              spacing={2}
              direction="column"
              p={2}
              className={classes.cardHeader}
              style={{ cursor: 'pointer' }}
              onClick={() => setOpenSwitcher(true)}
            >
              <P2GGrid item>
                <QuoteCardServiceLogo slug={quote.service.slug} width={140} dataTestId="sidebar-service-logo" />
              </P2GGrid>
              <P2GGrid item classNames={['large']}>
                <P2GText variant="subtitle2" component="p" p={0} m={0} dataTestId="sidebar-service-name">
                  {quote.service.name}
                </P2GText>
              </P2GGrid>
            </P2GGrid>
          </P2GGrid>
          {showServiceInfo && (
            <P2GGrid item>
              <P2GGrid container direction="column" p={2}>
                <P2GGrid item>
                  <Suspense>
                    <CmsContent contentKey="checkout-sidebar-service-description">
                      <DefaultServiceBenefits
                        title={t('SIDEBAR_SERVICE_BENEFITS_TITLE', { defaultValue: 'Why choose this service?' })}
                        currency={quote.currency}
                        isDropOff={isDropOff}
                        isLocker={isQuoteLockerDropOff(quote)}
                        requiresPrinter={requiresPrinter}
                        includedCoverAmountMinorCurrency={quote.service.includedProtection || 0}
                        quoteDates={quote.dates}
                      />
                    </CmsContent>
                  </Suspense>
                </P2GGrid>
              </P2GGrid>
            </P2GGrid>
          )}
          <P2GGrid item>
            <QuotePrice quote={quote} basket={item} protection={protection} />
          </P2GGrid>
          <Suspense>
            {openSwitcher && <BasketServiceSwitcher open={openSwitcher} onClose={() => setOpenSwitcher(false)} />}
          </Suspense>
        </P2GGrid>
      </P2GCardContainer>
      <P2GBox mt={2}>
        <LiveChat variant="SIDEBAR" />
      </P2GBox>
      <FeedbackButton wrapper={(x) => <P2GBox mt={2}>{x}</P2GBox>} />
    </>
  );
}

export default CheckoutSidebar;
